// TokenSelector — the global token segmented control (BTC · ETH · SOL · HYPE ·
// XRP · AVAX), twin of the WindowSelector. Unlike windows (which switch
// client-side off the one bundle), picking a token RE-REQUESTS the feed with the
// new token_pair. Populated from the payload's embedded `tokens[]` manifest. A
// thin (low-liquidity) token carries a small dot so the sparse dashboard isn't
// mistaken for missing data.

import { Stack, Box } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import { tokenLabel } from '../../../../providers/options-flow/options-flow.helpers';
import { TokenInfo } from '../../../../providers/options-flow/types';

interface TokenSelectorProps {
  value: string; // active token ticker
  tokenList: TokenInfo[];
  onChange: (token: string) => void;
}

export const TokenSelector = ({ value, tokenList, onChange }: TokenSelectorProps) => (
  <Stack
    direction="row"
    role="tablist"
    aria-label="Token"
    sx={{
      display: 'inline-flex',
      border: `1px solid ${tokens.border.default}`,
      borderRadius: '6px',
      overflow: 'hidden',
    }}
  >
    {tokenList.map((t, i) => {
      const active = t.token === value;
      const thin = t.liquidity === 'low';
      return (
        <Box
          key={t.token}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(t.token)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.4px',
            px: '11px',
            py: '4px',
            borderLeft: i === 0 ? 'none' : `1px solid ${tokens.border.default}`,
            color: active ? tokens.flow.accent : tokens.text.secondary,
            backgroundColor: active ? tokens.bg.widget : 'transparent',
            '&:hover': { backgroundColor: tokens.bg.widget },
          }}
        >
          {tokenLabel(t)}
          {thin && (
            // Small amber dot marking a thin (low-liquidity) token.
            <Box
              aria-hidden
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: tokens.flow.accent,
                opacity: 0.85,
              }}
            />
          )}
        </Box>
      );
    })}
  </Stack>
);
